<script lang="ts">
  import { onMount } from "svelte";
  import { listProducts } from "@shared/api/products";
  import { reqArray, reqRecord } from "@shared/value-objects/parse-helpers";

  /// 表示に必要な最小形。shared は domain (branded Product) を import できないため、
  /// API の unknown レスポンスからこの形だけを取り出す (value は plain string id)。
  /// domain `Product` (id: ProductId ⊆ string) は構造的に `ProductOption` へ代入可能なので
  /// 親が domain Product[] を `products` prop に渡しても通る。
  interface ProductOption {
    id: string;
    name: string;
  }

  interface Props {
    value: string;
    optional?: boolean;
    placeholder?: string;
    disabled?: boolean;
    id?: string;
    products?: ProductOption[] | null;
    onChange?: (value: string) => void;
  }

  let {
    value = $bindable(""),
    optional = true,
    placeholder = "商品を選択",
    disabled = false,
    id,
    products: providedProducts = null,
    onChange,
  }: Props = $props();

  let loadedProducts = $state<ProductOption[]>([]);
  let loaded = $state(false);
  let error = $state<string | null>(null);

  $effect(() => {
    if (providedProducts === null) return;
    loadedProducts = providedProducts;
    loaded = true;
    error = null;
  });

  onMount(() => {
    if (providedProducts !== null) return;
    void load();
  });

  async function load() {
    loaded = false;
    error = null;
    try {
      const res = await listProducts({
        page_size: 200,
        sort_by: "name",
        sort_dir: "asc",
      });
      const items = reqArray(reqRecord(res, "products").items, "products.items");
      loadedProducts = items.map((it) => {
        const o = reqRecord(it, "product");
        return { id: String(o.id ?? ""), name: String(o.name ?? "") };
      });
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
      loadedProducts = [];
    } finally {
      loaded = true;
    }
  }

  function handleChange() {
    onChange?.(value);
  }
</script>

<select bind:value {id} {disabled} onchange={handleChange}>
  {#if optional}
    <option value="">{placeholder}</option>
  {/if}
  {#each loadedProducts as product (product.id)}
    <option value={product.id}>{product.name}</option>
  {/each}
</select>
{#if !loaded}
  <small class="hint">読込中…</small>
{/if}
{#if error}
  <small class="error">商品の取得に失敗しました: {error}</small>
{/if}

<style>
  select {
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font: inherit;
    background: #fff;
  }

  .hint {
    color: #6b7280;
    font-size: 0.7rem;
  }

  .error {
    color: #b91c1c;
    font-size: 0.7rem;
  }
</style>
